import { addDays } from 'date-fns';
import * as R from 'ramda';

import { Plan, PlanSegment, PurchaseType } from '@/types';
import { snakeCaseKeys } from '@/utils/formatting/formatting';

export const transformDesignationsFormForApi = R.pipe(
  ({ userCountry, userDesignations, designationOptions }) => {
    if (!userDesignations) {
      return [
        {
          ...designationOptions.find(
            (d: any) => !d.designated && d.countryCode === userCountry,
          ),
          geographicRegion: null,
          certificationId: null,
          yearOfDesignation: null,
        },
      ];
    }

    const payload = designationOptions.map((d: any) => {
      if (!userDesignations[d.countryCode]?.[d.designation] || !d.designated)
        return null;

      const isCanCpa = d.countryCode === 'CAN' && d.designation === 'CPA';
      const isUsaCpa = d.countryCode === 'USA' && d.designation === 'CPA';
      const isUsaEa = d.countryCode === 'USA' && d.designation === 'EA';
      const isBmuCpa = d.countryCode === 'BMU' && d.designation === 'CPA';

      if (isCanCpa) {
        return {
          ...d,
          geographicRegion: Object.keys(userDesignations.CAN.CPA.provinces),
          yearOfDesignation: +userDesignations.CAN.CPA.year,
          certificationId: null,
        };
      }

      if (isUsaCpa) {
        return {
          ...d,
          geographicRegion: Object.keys(userDesignations.USA.CPA.states),
          yearOfDesignation: null,
          certificationId: null,
        };
      }

      if (isUsaEa) {
        return {
          ...d,
          geographicRegion: null,
          yearOfDesignation: null,
          certificationId: userDesignations.USA.EA,
        };
      }

      if (isBmuCpa) {
        return {
          ...d,
          geographicRegion: null,
          yearOfDesignation: +userDesignations.BMU.CPA.year,
          certificationId: null,
        };
      }

      return {
        ...d,
        geographicRegion: null,
        certificationId: null,
        yearOfDesignation: null,
      };
    });

    return R.filter(i => !R.isNil(i), payload);
  },
  R.map(snakeCaseKeys),
);

const B2BPlans = [
  PurchaseType.B2B_FREE,
  PurchaseType.B2B_PAID,
  PurchaseType.TEAM_TRIAL,
  PurchaseType.TEAM_PAID,
  PurchaseType.PAY_AS_YOU_GO,
];

export const transformPlan = (response: any[]): Plan => {
  try {
    const [plan] = response;
    if (!plan?.limit_details) return null;
    return R.pipe(
      p => ({
        planState: p.plan_state,
        validFrom: p.limit_details.valid_from,
        validUntil: plan.limit_details.expiry_date
          ? addDays(
              plan.limit_details.expiry_date,
              plan.limits?.expiry?.grace_period ?? 0,
            )
              .toISOString()
              .split('T')[0]
          : undefined,
        totalCredits: p.limit_details.total_credits ?? undefined,
        totalRedemptions: p.limit_details.total_redemptions ?? 0,
        cap: p.limit_details.cap ?? undefined,
        licenceValid: p.licence_valid,
        usageRemaining: calculateUsageRemaining(p),
        showUsageRemaining:
          !R.isNil(p.limit_details.cap) ||
          (p.segment === PlanSegment.B2C &&
            !R.isNil(p.limit_details.total_credits)),
        segment: p.segment,
        purchaseType: p.purchase_type,
        organizationName: p.organization_name,
        organizationId: p.organization_id,
        organizationMemberUsageCapValid: p.organization_member_usage_cap_valid,
        rules: p.rules ?? [],
        unenforcedLimits:
          p.limits?.usage?.enforce === false ||
          p.limits?.expiry?.enforce === false
            ? {
                totalCredits: p.limits.usage?.amount ?? undefined,
                validUntil: p.limits.expiry?.expiry_date || undefined,
              }
            : undefined,
      }),
      p => ({
        ...p,
        ...(p.validUntil === undefined &&
          p.totalCredits === undefined &&
          !B2BPlans.includes(p.purchaseType) && {
            totalCredits: 0,
            showUsageRemaining: true,
          }),
      }),
    )(plan);
  } catch (error) {
    throw new Error('Error transforming fetched plan', { cause: error });
  }
};

const calculateUsageRemaining = (p: any) => {
  if (R.isNil(p.limit_details.cap) && R.isNil(p.limit_details.total_credits))
    return undefined;

  if (!p.licence_valid) return 0;

  const limit = Math.min(
    p.limit_details.total_credits ?? Infinity,
    p.limit_details.cap ?? Infinity,
  );

  return Math.max(limit - p.limit_details.total_redemptions, 0);
};
